import { getHighContrastTextColor, SKY_COLORS } from './colors.js';
import { formatTime } from './formatters.js';
import { EARLY_MORNING_CUTOFF_HOUR } from '../constants/app.js';

export function getConditionMap(t) {
    return {
        clear: { color: SKY_COLORS.clear, label: t('colorGuide.clear') },
        partly_cloudy: { color: SKY_COLORS.partly_cloudy, label: t('colorGuide.partly_cloudy') },
        cloudy: { color: SKY_COLORS.cloudy, label: t('colorGuide.cloudy') },
        rain: { color: SKY_COLORS.rain, label: t('colorGuide.rain') },
        scattered_rain: { color: SKY_COLORS.scattered_rain, label: t('colorGuide.scattered_rain') },
        sleet: { color: SKY_COLORS.sleet, label: 'Sleet' },
        scattered_sleet: { color: SKY_COLORS.scattered_sleet, label: 'Scattered sleet' },
        hail: { color: SKY_COLORS.hail, label: 'Hail' },
        scattered_hail: { color: SKY_COLORS.scattered_hail, label: 'Scattered hail' },
        snow: { color: SKY_COLORS.snow, label: t('colorGuide.snow') },
        scattered_snow: { color: SKY_COLORS.scattered_snow, label: t('colorGuide.scattered_snow') }
    };
};

/**
 * Generates displayable segments for a weather timeline.
 * A special segment is created for the past (from the start time until now),
 * and weather data is rendered only from the current time forward.
 * @param {object} d The input data object.
 * @param {number} d.time The start time of the timeline (Unix timestamp).
 * @param {number} [d.length] The duration of the timeline in seconds. Defaults to 24 hours.
 * @param {Array<object>} [d.skyData] An array of weather data points.
 * @returns {Array<object>} An array of segment objects for display.
 */
/**
 * Calculates consistent timeline metrics based on a start time and settings.
 * This ensures that all parts of the UI (like segment bars and tick marks)
 * use the exact same start time, end time, and total duration for their calculations,
 * preventing alignment issues.
 *
 * @param {number} startTime - The Unix timestamp for the beginning of the day (e.g., midnight).
 * @param {boolean} hideEarlyMorning - Flag to determine if the timeline should start later.
 * @returns {{timelineStart: number, timelineEnd: number, totalHours: number, totalDuration: number}}
 */
const getTimelineConfig = (startTime, hideEarlyMorning) => {
    const totalHours = 24 - (hideEarlyMorning ? EARLY_MORNING_CUTOFF_HOUR : 0);
    const timelineStart = startTime + (hideEarlyMorning ? EARLY_MORNING_CUTOFF_HOUR * 60 * 60 : 0);
    const timelineEnd = startTime + 24 * 60 * 60;
    const totalDuration = totalHours * 3600;

    return {
        timelineStart,
        timelineEnd,
        totalHours,
        totalDuration
    };
};


export function segmentsFrom(d, conditionMap, hideEarlyMorning) {

    // 1. SETUP TIME AND BOUNDARIES using the shared configuration function
    const { timelineStart, timelineEnd, totalDuration } = getTimelineConfig(d.time, hideEarlyMorning);
    const now = Math.floor(Date.now() / 1000);

    // 2. PREPARE DATA
    const segments = [];

    // 3. CREATE THE "PAST" SEGMENT (if applicable)
    // This segment goes from the start of the timeline up to the present moment.
    // Only add a past segment if 'now' is after the effective timelineStart
    if (now > timelineStart) {
        segments.push({
            start: timelineStart,
            end: Math.min(now, timelineEnd), // Ensure past segment doesn't go beyond timelineEnd
            color: null, // Visually distinct from data-driven segments
        });
    }

    // 4. BUILD THE "PRESENT & FUTURE" SEGMENTS
    // The part of the timeline where we display weather data starts at 'now' or 'timelineStart'.
    const dataDisplayStart = Math.max(timelineStart, now);

    let currentTime = dataDisplayStart;

    // Find the initial point for conditions, considering the new dataDisplayStart
    let initialPoint = d.skyData.findLast(p => p.time <= dataDisplayStart);
    let currentConditions = initialPoint ? conditionMap[initialPoint.conditions] : null;

    // Filter for points that will define the start of a *new* segment in the future,
    // and are within the effective timeline.
    const futurePoints = d.skyData.filter(p => p.time > dataDisplayStart && p.time <= timelineEnd);

    for (const point of futurePoints) {
        // Add segment from the last point up to this new point
        if (currentTime < point.time && currentConditions) {
            segments.push({ start: currentTime, end: point.time, ...currentConditions });
        }

        currentTime = point.time;
        currentConditions = conditionMap[point.conditions];
    }

    // Add the final segment that goes from the last point to the end of the timeline.
    if (currentTime < timelineEnd && currentConditions) {
        segments.push({ start: currentTime, end: timelineEnd, ...currentConditions });
    }

    // 5. POST-PROCESS ALL SEGMENTS to add widths and text colors
    return segments.map(seg => {
        // Width is calculated relative to the *total* duration of the bar.
        const widthPct = ((seg.end - seg.start) / totalDuration) * 100;
        const textColor = seg.color ? getHighContrastTextColor(seg.color) : undefined;
        return { ...seg, widthPct, textColor };
    }).filter(seg => seg.widthPct > 0); // Filter out segments with zero width
}

export function timelineTicks(day, tz, hideEarlyMorning) {

    const { timelineStart, totalHours } = getTimelineConfig(day.time, hideEarlyMorning);

    const labelStep = hideEarlyMorning ? 3 : 4;
    const ticks = [];

    for (let i = 1; i < totalHours; i++) {
        const time = timelineStart + i * 3600;
        const xPct = i / totalHours * 100;
        const major = (i - 2) % labelStep === 0 && i !== 0 && i !== totalHours;
        let label = '';
        if (major) {
            label = formatTime(time, tz);
        }
        ticks.push({ time, xPct, major, label });
    }
    return ticks;
}
